import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Flex,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Menu,
    MenuButton,
    MenuDivider,
    MenuList,
    MenuItem,
    HStack,
    Icon,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { listScheduledJobs } from "services/ScheduleServices";
import { Header } from "build-ts/metadata";
import { useUser } from "context/UserContext";
import { HeaderWithStats } from "services/MetadataServices";
import { BsRobot } from "react-icons/bs";
import { ChevronDownIcon } from "@chakra-ui/icons";
import JobList from "modules/analyst/containers/JobList";

interface AnalysesTabProps {
    isTabActive: boolean;
    analystsList: HeaderWithStats[];
    isLoadingAnalysts: boolean;
}

const AnalysesTab: React.FC<AnalysesTabProps> = ({ isTabActive, analystsList, isLoadingAnalysts }) => {
    const navigate = useNavigate();
    const { defaultAnalystId } = useUser();

    const [selectedJobsAnalyst, setSelectedJobsAnalyst] = useState<Header | null>(null);
    const [jobs, setJobs] = useState<Header[]>([]);
    const [isLoadingJobs, setIsLoadingJobs] = useState(false);
    const [jobsSearchQuery, setJobsSearchQuery] = useState("");

    useEffect(() => {
        if (isLoadingAnalysts || !isTabActive) return;

        if (defaultAnalystId) {
            const analyst = analystsList.find(a => a.id === defaultAnalystId) || null;
            setSelectedJobsAnalyst(analyst);
            fetchJobs(analyst);
        } else {
            fetchJobs(null);
        }
    }, [defaultAnalystId, analystsList, isLoadingAnalysts, isTabActive]);

    const fetchJobs = async (analystFilter?: Header | null) => {
        setIsLoadingJobs(true);
        const response = await listScheduledJobs(
            "SCENARIO_JOB",
            -1,
            1,
            analystFilter?.id || "",
            "LAST_MODIFIED",
            false,
            true,
            ""
        );
        setJobs(response || []);
        setIsLoadingJobs(false);
    };

    const handleJobsAnalystSelect = (analyst: Header | null) => {
        setSelectedJobsAnalyst(analyst);
        fetchJobs(analyst);
    };

    const filteredJobs = useMemo(() => {
        if (!jobsSearchQuery.trim()) return jobs;
        const lower = jobsSearchQuery.toLowerCase();
        return jobs.filter(j => j.name?.toLowerCase().includes(lower));
    }, [jobs, jobsSearchQuery]);

    const handleJobClick = (job: Header) => {
        const analystId = selectedJobsAnalyst?.id || job.owner_id;
        if (analystId && job.id) {
            navigate(`/analyst/${analystId}/jobs/${job.id}`);
        }
    };

    return (
        <>
            {/* Search Bar */}
            <InputGroup mb={5} size="md">
                <InputLeftElement pointerEvents="none" h="40px">
                    <Icon as={FiSearch} boxSize={5} color="purple.700" />
                </InputLeftElement>
                <Input
                    placeholder="Search workflows..."
                    value={jobsSearchQuery}
                    onChange={(e) => setJobsSearchQuery(e.target.value)}
                    bg="white"
                    border="1px solid"
                    borderColor="purple.800"
                    borderRadius="lg"
                    fontSize="md"
                    color="fg.default"
                    _placeholder={{ color: "purple.800" }}
                    _focus={{
                        borderColor: "purple.700",
                        boxShadow: "none",
                        borderWidth: "2px",
                        bg: "purple.50",
                    }}
                />
            </InputGroup>

            {/* Count & Analyst Dropdown */}
            <Flex align="center" justify="space-between" mb={3} px={1}>
                <Text fontSize="sm" color="gray.500" fontWeight="600" align="left" m={0}>
                    Showing {filteredJobs.length} {filteredJobs.length !== 1 ? "workflows" : "workflow"}
                </Text>

                {/* Analyst Filter */}
                <Menu>
                    <MenuButton
                        as={Button}
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                        bg="purple.50"
                        borderColor="purple.50"
                        _hover={{ bg: "purple.100", borderColor: "purple.100" }}
                        borderRadius="full"
                        borderWidth="1px"
                        px={3}
                        py={1}
                    >
                        <HStack spacing={2}>
                            <Icon as={BsRobot} boxSize={5} />
                            <Text fontSize="sm" color="fg.default" fontWeight="500" m={0} noOfLines={1}>
                                {selectedJobsAnalyst ? selectedJobsAnalyst.name : "All analysts"}
                            </Text>
                            <Icon as={ChevronDownIcon} boxSize={5} />
                        </HStack>
                    </MenuButton>
                    <MenuList
                        maxH="250px"
                        overflowY="auto"
                        minW="200px"
                        borderColor="purple.200"
                        boxShadow="lg"
                    >
                        <MenuItem
                            fontSize="sm"
                            bg={!selectedJobsAnalyst ? "purple.700" : "transparent"}
                            _hover={{ bg: !selectedJobsAnalyst ? "purple.600" : "purple.50" }}
                            onClick={() => handleJobsAnalystSelect(null)}
                        >
                            <HStack spacing={2}>
                                <Icon as={BsRobot} boxSize={5} color={!selectedJobsAnalyst ? "white" : "purple.700"} />
                                <Text fontSize="sm" color={!selectedJobsAnalyst ? "white" : "fg.default"} fontWeight="500" m={0}>
                                    All analysts
                                </Text>
                            </HStack>
                        </MenuItem>
                        {analystsList.length > 0 && <MenuDivider />}
                        {analystsList.length === 0 ? (
                            <MenuItem isDisabled fontSize="sm" color="purple.500">
                                No analysts found
                            </MenuItem>
                        ) : (
                            analystsList.map((analyst) => (
                                <MenuItem
                                    key={analyst.id}
                                    fontSize="sm"
                                    bg={selectedJobsAnalyst?.id === analyst.id ? "purple.700" : "transparent"}
                                    _hover={{ bg: selectedJobsAnalyst?.id === analyst.id ? "purple.600" : "purple.50" }}
                                    onClick={() => handleJobsAnalystSelect(analyst)}
                                >
                                    <HStack spacing={2}>
                                        <Icon
                                            as={BsRobot}
                                            boxSize={5}
                                            color={selectedJobsAnalyst?.id === analyst.id ? "white" : "purple.700"}
                                        />
                                        <Text
                                            fontSize="sm"
                                            color={selectedJobsAnalyst?.id === analyst.id ? "white" : "fg.default"}
                                            fontWeight="500"
                                            m={0}
                                            noOfLines={1}
                                        >
                                            {analyst.name}
                                        </Text>
                                    </HStack>
                                </MenuItem>
                            ))
                        )}
                    </MenuList>
                </Menu>
            </Flex>

            {/* Job List */}
            <Box flex={1} minH={0} h="calc(100vh - 260px)">
                <JobList
                    jobs={filteredJobs}
                    isLoadingJobs={isLoadingJobs}
                    onJobClick={handleJobClick}
                    onRefresh={() => fetchJobs(selectedJobsAnalyst)}
                />
            </Box>
        </>
    );
};

export default AnalysesTab; 
